import { Component, OnDestroy, OnInit, HostListener, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Subject, interval } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { AuthService } from '../services/auth.service';
import { ApiService } from '../services/api.service';
import { ShellEventsService } from '../services/shell-events.service';
import { PlanService } from '../services/plan.service';
import { DairyAddonService } from '../services/dairy-addon.service';
import { PermissionService } from '../services/permission.service';
import { OfflineService } from '../services/offline.service';
import { LanguageService, Language } from '../services/language.service';
import { NotificationService } from '../services/notification.service';
import { TransactionTerminologyMode, TransactionTerminologyService } from '../services/transaction-terminology.service';
import { NAV_ITEMS, NAV_SECTIONS, NavItem } from '../constants/nav-items.const';
import { TabService, AppTab } from '../services/tab.service';
import { BluetoothPrintService } from '../services/bluetooth-print.service';
import { APP_VERSION } from '../../../version';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private clockInterval: any;
    readonly isPlayStoreBuild = environment.isPlayStoreBuild;
    sidebarCollapsed = true;
    readonly noOpBtCallback = (): void => {}; // used by sidebar BT Printer button (pair-only, no print)
    mobileOpen = false;
    desktopSidebarOpen = false;
    isDarkTheme = false;
    showProfile = false;
    appVersion = APP_VERSION;
    liveDate = '';
    liveTime = '';

    // Account dialogs
    showEditDairy = false;
    showChangePassword = false;
    showDeleteAccount = false;
    showEditProfile = false;
    savingDairy = false;
    savingPassword = false;
    savingProfile = false;
    editDairyMsg = '';
    changePwMsg = '';
    showPwdSessionChoice = false;
    pwdChangeToken = '';
    editProfileMsg = '';
    deleteConfirmText = '';
    deleteAccountPassword = '';
    deleteAccountPasswordVisible = false;
    deleteAccountMsg = '';
    deletingAccount = false;
    dairyForm!: FormGroup;
    passwordForm!: FormGroup;
    profileForm!: FormGroup;

    // Create Dairy (first-login wizard)
    showCreateDairy = false;
    createDairyForm!: FormGroup;
    creatingDairy = false;
    createDairyDbError = false;
    createDairyMsg = '';

    /** Current Indian financial year, e.g. "2025-2026" */
    private get finYear(): string {
        const now = new Date();
        const y = now.getFullYear();
        return now.getMonth() >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
    }

    /** Minimal default chart-of-accounts the API needs to build ledger config */
    private readonly defaultPreAccounts = [
        { ac_name: 'Cash A/c',                 group_name: 'Cash-in-hand',   g_id: 1,  code: 600000, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Cash A/c',                 op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Milk Purchase A/c',        group_name: 'Purchases',      g_id: 9,  code: 600001, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Milk Purchase A/c',        op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Purchase A/c',             group_name: 'Purchases',      g_id: 9,  code: 600002, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Purchase A/c',             op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Milk Sales A/c',           group_name: 'Sales',          g_id: 10, code: 600003, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Milk Sales A/c',           op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Sales A/c',                group_name: 'Sales',          g_id: 10, code: 600004, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Sales A/c',                op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Commission Paid A/c',      group_name: 'Expenses',       g_id: 4,  code: 600005, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Commission Paid A/c',      op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Commission Received A/c',  group_name: 'Incomes',        g_id: 5,  code: 600006, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Commission Received A/c',  op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Discount Allowed A/c',     group_name: 'Expenses',       g_id: 4,  code: 600007, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Discount Allowed A/c',     op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Discount Received A/c',    group_name: 'Incomes',        g_id: 5,  code: 600008, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Discount Received A/c',    op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Round Off A/c',            group_name: 'Expenses',       g_id: 4,  code: 600009, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Round Off A/c',            op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Bonus Paid A/c',           group_name: 'Expenses',       g_id: 4,  code: 600010, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Bonus Paid A/c',           op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Can Handling Paid A/c',    group_name: 'Expenses',       g_id: 4,  code: 600011, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Can Handling Paid A/c',    op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Head Load Paid A/c',       group_name: 'Expenses',       g_id: 4,  code: 600012, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Head Load Paid A/c',       op_cr_dr: 'Dr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Head Load Received A/c',   group_name: 'Incomes',        g_id: 5,  code: 600013, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Head Load Received A/c',   op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'Can Handling Received A/c',group_name: 'Incomes',        g_id: 5,  code: 600014, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'Can Handling Received A/c',op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'IGST A/c',                 group_name: 'Duties & Taxes', g_id: 11, code: 600015, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'IGST A/c',                 op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'CGST A/c',                 group_name: 'Duties & Taxes', g_id: 11, code: 600016, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'CGST A/c',                 op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
        { ac_name: 'SGST A/c',                 group_name: 'Duties & Taxes', g_id: 11, code: 600017, route_id: 0, rate_list_id: 0, route_name: '', rate_list: '', print_name: 'SGST A/c',                 op_cr_dr: 'Cr', op_date: new Date().toISOString().slice(0,10), st_date: new Date().toISOString().slice(0,10), address: '', city: '', mob: '' },
    ];

    readonly currentYear = new Date().getFullYear();

    pageTitle = 'nav.dashboard';
    currentUser: any = null;

    // Menu Search (topbar)
    menuSearch = '';
    menuSearchOpen = false;
    menuSearchResults: NavItem[] = [];
    menuSearchActiveIndex = -1;
    @ViewChild('menuSearchInput') menuSearchInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('tabBarViewport') tabBarViewportRef?: ElementRef<HTMLDivElement>;
    @ViewChildren('tabSwitcherItem') tabSwitcherItemRefs?: QueryList<ElementRef<HTMLDivElement>>;

    // Bottom drawer
    showBottomDrawer = false;
    bottomDrawerPlacement: 'bottom' | 'right' = 'bottom';
    readonly navSections = NAV_SECTIONS;
    readonly transactionTerminologyOptions: Array<{ value: TransactionTerminologyMode; label: string }> = [
        { value: 'credit_debit', label: 'Credit / Debit' },
        { value: 'in_out', label: 'In / Out' },
    ];

    // Sidebar Search (mobile)
    sidebarSearch = '';

    // Language switcher
    showLangMenu = false;
    get languages(): Language[] { return this.languageService.languages; }
    get currentLang(): string   { return this.languageService.current; }

    navItems: NavItem[] = NAV_ITEMS;
    visibleTabs: AppTab[] = [];
    overflowTabs: AppTab[] = [];
    showTabOverflowMenu = false;
    showTabSwitcher = false;
    tabSwitcherIndex = 0;
    private lastViewportWidth = 0;
    private lastViewportBucket: 'mobile' | 'tablet' | 'desktop' = 'desktop';

    private get effectiveViewportWidth(): number {
        const w = window.innerWidth || document.documentElement.clientWidth || 1024;
        return this.isNativeTabletDevice ? Math.min(w, 1024) : w;
    }

    private get isNativeTabletDevice(): boolean {
        if (!Capacitor.isNativePlatform()) return false;
        const ua = navigator.userAgent || '';
        const isIpad = /iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isAndroidTabletUa = /Android/i.test(ua) && !/Mobile/i.test(ua);
        const minSide = Math.min(window.innerWidth || 0, window.innerHeight || 0);
        const looksTabletBySize = minSide >= 600;
        return isIpad || isAndroidTabletUa || looksTabletBySize;
    }

    get isNativeTabletLayout(): boolean {
        return this.isNativeTabletDevice;
    }

    private getViewportBucket(width: number): 'mobile' | 'tablet' | 'desktop' {
        if (width <= 768) return 'mobile';
        if (width <= 1024) return 'tablet';
        return 'desktop';
    }

    /** Nav items filtered by the logged-in user's permissions */
    get visibleNavItems() {
        const isMobileViewport = this.effectiveViewportWidth <= 768;
        const mobileHiddenRoutes = new Set(['/gst-sales', '/gst-purchases']);
        return this.navItems.filter(item =>
            (item.alwaysVisible || this.permissionService.isAllowed(item.module as any, 'view')) &&
            !(isMobileViewport && mobileHiddenRoutes.has(item.route || ''))
        );
    }

    // All shortcuts available for pinning
    readonly allShortcuts = [
        { key: 'milk-collection-q', label: 'Milk Collection(Q)', icon: '🚜', route: '/milk-collection', soon: false, module: 'milk_collection' },
        { key: 'milk-sale-q',       label: 'Milk Sale(Q)',        icon: '🥛', route: '/milk-sale',       soon: false, module: 'milk_sale'       },
        { key: 'payments',          label: 'Payments',            icon: '₹',  route: '/payments',       soon: false, module: 'payments'        },
        { key: 'receipts',          label: 'Receipts',            icon: '🧾', route: '/receipts',        soon: false, module: 'receipts'        },
        { key: 'members',           label: 'Members / Supplier',  icon: '👥', route: '/members',        soon: false, module: 'members'         },
        { key: 'products',          label: 'Products',            icon: '📦', route: '/products',       soon: false, module: 'products'        },
        { key: 'purchases',         label: 'Purchase Voucher',    icon: '🛒', route: '/purchases',      soon: false, module: 'purchases'       },
        { key: 'sales',             label: 'Sale Voucher',        icon: '🏷️', route: '/sales',          soon: false, module: 'sales'           },
        { key: 'gst-sales',          label: 'GST Sale',            icon: '🧾', route: '/gst-sales',      soon: false, module: 'gst_sales'       },
        { key: 'gst-purchases',      label: 'GST Purchase',        icon: '🧾', route: '/gst-purchases',  soon: false, module: 'gst_purchases'   },
        { key: 'routes',            label: 'Routes',              icon: '🗺️', route: '/routes',         soon: false, module: 'routes'          },
        { key: 'milk-rates',        label: 'Milk Rates',          icon: '💰', route: '/rate-list',      soon: false, module: 'rate_list'       },
        { key: 'daybook',           label: 'Daybook',             icon: '📒', route: '/daybook',        soon: false, module: 'daybook'         },
        { key: 'ledger',            label: 'Ledger Statement',    icon: '📒', route: '/ledger',         soon: false, module: 'ledger'          },
        { key: 'ledger-reports',    label: 'Ledger Reports',      icon: '📊', route: null,              soon: true,  module: null              },
        { key: 'bulk-milk-bills',   label: 'Bulk Milk Bills',     icon: '📋', route: null,              soon: true,  module: null              },
        { key: 'member-reports',    label: 'Member Reports',      icon: '👤', route: null,              soon: true,  module: null              },
    ];

    /** Shortcuts filtered to only those the current user has permission to view */
    get visibleShortcuts() {
        return this.allShortcuts.filter(s =>
            !s.module || this.permissionService.isAllowed(s.module as any, 'view')
        );
    }

    pinnedKeys: string[] = [];
    showPinPicker = false;

    // System announcement
    sysAnnouncement: { id: number; title: string; message: string; type: string } | null = null;
    sysAnnDismissed = false;
    showAnnouncementModal = false;

    // Dairy stats
    dairyStats: { totalMembers: number; activeMembers: number; totalSuppliers: number; activeSuppliers: number } | null = null;
    statsLoading = false;

    // Plan helpers
    get planType(): string { return this.planService.getPlanType(); }
    get planBadgeClass(): string { return { BASIC: 'plan-basic', ADVANCE: 'plan-advance', PREMIUM: 'plan-premium' }[this.planType] || 'plan-basic'; }
    get expiryDate(): string { return this.dairy?.amc_date || this.dairy?.expiry_date || ''; }
    get daysToExpiry(): number {
        if (!this.expiryDate) return -1;
        const diff = new Date(this.expiryDate).getTime() - Date.now();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    }
    get expiryLabel(): string {
        if (String(this.dairy?.subscription_status || '').toUpperCase() === 'PENDING') return 'Subscription required';
        const d = this.daysToExpiry;
        if (d < 0) return 'Expired';
        if (d === 0) return 'Expires today';
        if (d <= 7) return `${d} day${d === 1 ? '' : 's'} left`;
        return this.expiryDate.split('T')[0];
    }
    get expiryUrgent(): boolean { return this.daysToExpiry >= 0 && this.daysToExpiry <= 15; }

    get pinnedShortcuts() {
        return this.visibleShortcuts.filter(s => this.pinnedKeys.includes(s.key));
    }

    loadPins(): void {
        const saved = localStorage.getItem('crm-pins');
        this.pinnedKeys = saved ? JSON.parse(saved) : ['members'];
    }

    loadBottomDrawerPlacement(): void {
        const saved = localStorage.getItem('crm-bottom-drawer-placement');
        this.bottomDrawerPlacement = saved === 'right' ? 'right' : 'bottom';
    }

    loadAnnouncement(): void {
        this.sysAnnouncement = null;
        this.sysAnnDismissed = false;
        this.showAnnouncementModal = false;
        this.api.get<any>('get_system_announcement.php').subscribe({
            next: (res: any) => {
                if (res?.success && res?.announcement) {
                    this.sysAnnouncement = res.announcement;
                    this.sysAnnDismissed = localStorage.getItem(
                        this.announcementDismissKey(res.announcement.id)
                    ) === '1';
                    this.showAnnouncementModal = !this.sysAnnDismissed;
                }
            },
            error: () => { /* silently ignore — non-critical */ }
        });
    }

    dismissAnnouncement(): void {
        if (!this.sysAnnouncement) return;
        this.sysAnnDismissed = true;
        this.showAnnouncementModal = false;
        localStorage.setItem(this.announcementDismissKey(this.sysAnnouncement.id), '1');
    }

    openAnnouncement(): void {
        if (!this.sysAnnouncement) return;
        this.showAnnouncementModal = true;
        this.closeMobile();
        this.closeDesktopSidebar();
    }

    get announcementTypeLabel(): string {
        const labels: Record<string, string> = {
            info: 'Information',
            warning: 'Important notice',
            critical: 'Critical alert',
        };
        return labels[this.sysAnnouncement?.type || 'info'] || labels['info'];
    }

    get announcementIcon(): string {
        const icons: Record<string, string> = {
            info: 'pi-info-circle',
            warning: 'pi-exclamation-triangle',
            critical: 'pi-exclamation-circle',
        };
        return icons[this.sysAnnouncement?.type || 'info'] || icons['info'];
    }

    private announcementDismissKey(announcementId: number): string {
        const userId = this.currentUser?.user_id || this.currentUser?.mu_id || this.currentUser?.id || 'anonymous';
        return `sysAnnDismissed:${userId}:${announcementId}`;
    }

    loadDairyStats(): void {
        const dId = this.dairy?.id;
        if (!dId) return;
        this.statsLoading = true;
        this.api.get<any>(`members_and_accounts/get_accounts_analytics.php?d_id=${dId}`).subscribe({
            next: (res: any) => {
                this.statsLoading = false;
                if (res?.success && res?.data) {
                    const rows: any[] = res.data;
                    const members   = rows.filter((r: any) => r.member_type === 'Premium Members');
                    const suppliers = rows.filter((r: any) => r.member_type === 'Regular Members');
                    this.dairyStats = {
                        totalMembers:    members.reduce((s: number, r: any) => s + +r.member_count, 0),
                        activeMembers:   members.filter((r: any) => r.member_status === 'ACTIVE').reduce((s: number, r: any) => s + +r.member_count, 0),
                        totalSuppliers:  suppliers.reduce((s: number, r: any) => s + +r.member_count, 0),
                        activeSuppliers: suppliers.filter((r: any) => r.member_status === 'ACTIVE').reduce((s: number, r: any) => s + +r.member_count, 0),
                    };
                }
            },
            error: () => { this.statsLoading = false; }
        });
    }

    togglePin(key: string): void {
        const idx = this.pinnedKeys.indexOf(key);
        if (idx > -1) {
            this.pinnedKeys.splice(idx, 1);
        } else if (this.pinnedKeys.length < 6) {
            this.pinnedKeys.push(key);
        }
        localStorage.setItem('crm-pins', JSON.stringify(this.pinnedKeys));
    }

    isPinned(key: string): boolean {
        return this.pinnedKeys.includes(key);
    }

    constructor(
        private authService: AuthService,
        private router: Router,
        private fb: FormBuilder,
        private api: ApiService,
        private shellEvents: ShellEventsService,
        public planService: PlanService,
        public addons: DairyAddonService,
        public permissionService: PermissionService,
        public offlineService: OfflineService,
        public languageService: LanguageService,
        public transactionTerminology: TransactionTerminologyService,
        private notificationService: NotificationService,
        public tabService: TabService,
        public btService: BluetoothPrintService,
    ) { }

    /** Returns true if a nav item's plan feature is locked for the current user. */
    isNavLocked(item: any): boolean {
        if (this.planService.isRenewalOnlyAccess()
            && !['/dashboard', '/store'].includes(String(item.route || ''))) return true;
        if (!item.planFeature) return false;
        return !this.planService.isFeatureAllowed(item.planFeature);
    }
    reloadAddons(): void {
        this.addons.refresh().pipe(takeUntil(this.destroy$)).subscribe({ error: () => {} });
    }

    get dairy(): any { return this.authService.getCurrentDairy(); }
    get dairySubscriptionStatus(): string {
        return String(this.dairy?.subscription_status || 'ACTIVE').toUpperCase();
    }

    switchDairy(event: Event): void {
        const dairyId = Number((event.target as HTMLSelectElement).value);
        if (!this.authService.selectDairy(dairyId)) return;
        this.tabService.closeAll();
        window.location.href = `${window.location.origin}/#/dashboard`;
    }

    ngOnDestroy(): void {
        if (this.clockInterval) {
            clearInterval(this.clockInterval);
        }
        // Clear all open tabs and destroy cached component trees on logout
        this.tabService.closeAll();
        this.destroy$.next();
        this.destroy$.complete();
        this.notificationService.stopPolling();
    }

    switchLanguage(code: string): void {
        this.languageService.use(code);
        this.showLangMenu = false;
    }

    ngOnInit(): void {
        this.addons.refresh().pipe(takeUntil(this.destroy$)).subscribe({ error: () => {} });
        this.currentUser = this.authService.getCurrentUser();
        this.lastViewportWidth = this.effectiveViewportWidth;
        this.lastViewportBucket = this.getViewportBucket(this.lastViewportWidth);

        const tick = () => {
            const now = new Date();
            this.liveDate = now.toLocaleDateString('en-IN', {
                weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
            });
            this.liveTime = now.toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
            });
        };
        tick();
        this.clockInterval = setInterval(tick, 1000);

        // Super-admin should never land on the regular shell — redirect to their panel
        if (this.permissionService.isSuperAdmin()) {
            this.router.navigate(['/super-admin/dashboard']);
            return;
        }

        // Init language: localStorage > dairy config > 'en'
        const configLang = this.dairy?.configurations?.app_language;
        this.languageService.init(configLang);
        this.loadPins();
        this.loadBottomDrawerPlacement();
        this.loadDairyStats();
        this.loadAnnouncement();
        this.authService.refreshCurrentUser().subscribe();

        // ── First-login check: no dairies → show creation wizard (admin only) ──
        if (!this.currentUser?.dairies?.length && this.permissionService.isDairyAdmin()) {
            this.openCreateDairy();
        }

        // ── Silently pre-cache all offline data so the app works without internet ──
        const dId = this.authService.getDairyId();
        // Run once for the initial online state, then only when offline becomes online again.
        this.offlineService.isOnline$.pipe(
            distinctUntilChanged(),
            filter(online => online),
            takeUntil(this.destroy$)
        ).subscribe(() => this.offlineService.preloadAllOfflineData(dId));

        // ── Session heartbeat: refresh user data every 5 minutes ──────────────
        // Keeps permissions and active-status in sync without requiring re-login.
        // If the server signals the user is deactivated or the JWT is invalid,
        // the API interceptor automatically forces logout.
        interval(5 * 60 * 1000).pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.authService.refreshCurrentUser().subscribe());

        this.authService.userRefreshed$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(user => {
            this.currentUser = user;
            this.addons.refresh().pipe(takeUntil(this.destroy$)).subscribe({ error: () => {} });
        });

        this.shellEvents.events$.pipe(takeUntil(this.destroy$)).subscribe(evt => {
            if (evt === 'editDairy') this.openEditDairy();
        });

        const saved = localStorage.getItem('crm-theme') || 'light';
        this.isDarkTheme = saved === 'dark';
        this.applyTheme();

        // ── Notifications ──────────────────────────────────────────────────────
        this.notificationService.init('dairy');

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe((event: any) => {
            const url = (event as NavigationEnd).urlAfterRedirects || this.router.url;
            const found = this.findNavItemForUrl(url);
            this.pageTitle = found ? found.labelKey : 'brand.name';
            this.mobileOpen = false; // close drawer on navigation
            this.desktopSidebarOpen = false;
            this.showBottomDrawer = false; // close bottom drawer on navigation
            // Auto-register a tab for any page the user navigates to
            if (found) {
                this.tabService.addTabWithoutNavigating(found.route, found.label, found.icon, found.labelKey);
            }
            this.scheduleTabOverflowRecalc();
        });

        this.tabService.tabs$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.scheduleTabOverflowRecalc();
        });

        this.tabService.activeId$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.scheduleTabOverflowRecalc();
        });

        // Register a tab for the page that is already active at startup
        const initialUrl = this.router.url;
        const initialFound = this.findNavItemForUrl(initialUrl);
        if (initialFound) {
            this.tabService.addTabWithoutNavigating(initialFound.route, initialFound.label, initialFound.icon, initialFound.labelKey);
        }
        this.scheduleTabOverflowRecalc();
    }

    private findNavItemForUrl(rawUrl: string): NavItem | undefined {
        const url = (rawUrl || '').split('?')[0].split('#')[0];
        const candidates = this.navItems
            .filter(item => !!item.route)
            .filter(item => item.route !== '')
            .filter(item => url === item.route || url.startsWith(item.route + '/'))
            .sort((a, b) => b.route.length - a.route.length);
        return candidates[0];
    }

    toggleSidebar(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        this.scheduleTabOverflowRecalc();
    }

    toggleMobile(): void {
        this.mobileOpen = !this.mobileOpen;
    }

    toggleDesktopSidebar(): void {
        this.desktopSidebarOpen = !this.desktopSidebarOpen;
        if (this.desktopSidebarOpen) {
            this.sidebarCollapsed = false;
        }
    }

    closeDesktopSidebar(): void {
        this.desktopSidebarOpen = false;
    }

    closeMobile(): void {
        this.mobileOpen = false;
        this.sidebarSearch = '';
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        const width = this.effectiveViewportWidth;
        const bucket = this.getViewportBucket(width);
        const bucketChanged = bucket !== this.lastViewportBucket;
        const widthChangedEnough = Math.abs(width - this.lastViewportWidth) > 24;

        // Mobile browsers fire frequent resize events on URL-bar show/hide while scrolling.
        // Ignore those unless width or breakpoint bucket actually changed.
        if (!bucketChanged && !widthChangedEnough) {
            return;
        }

        this.lastViewportWidth = width;
        this.lastViewportBucket = bucket;

        if (width <= 1024) {
            this.desktopSidebarOpen = false;
        } else {
            this.mobileOpen = false;
        }
        this.scheduleTabOverflowRecalc();
    }

    @HostListener('document:keydown', ['$event'])
    onGlobalKeydown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Tab' && this.tabService.tabs.length > 1) {
            event.preventDefault();
            this.openTabSwitcher();
            this.moveTabSwitcherSelection(event.shiftKey ? -1 : 1);
            return;
        }
        if (event.altKey && event.key.toLowerCase() === 'w' && this.tabService.tabs.length > 1) {
            event.preventDefault();
            this.showTabSwitcher ? this.closeTabSwitcher() : this.openTabSwitcher();
            return;
        }
        if (this.showTabSwitcher) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeTabSwitcher();
                return;
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.moveTabSwitcherByDirection('right');
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.moveTabSwitcherByDirection('left');
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.moveTabSwitcherByDirection('down');
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.moveTabSwitcherByDirection('up');
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                this.activateTabSwitcherSelection();
                return;
            }
        }
        if ((event.ctrlKey || event.metaKey) && event.key === '/') {
            event.preventDefault();
            this.openMenuSearch();
        }
        if (event.key === 'Escape' && this.menuSearchOpen) {
            this.closeMenuSearch();
        }
    }

    onMenuSearchKeydown(event: KeyboardEvent): void {
        const len = this.menuSearchResults.length;
        if (!len) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.menuSearchActiveIndex = (this.menuSearchActiveIndex + 1) % len;
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.menuSearchActiveIndex = (this.menuSearchActiveIndex - 1 + len) % len;
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const target = this.menuSearchActiveIndex >= 0
                ? this.menuSearchResults[this.menuSearchActiveIndex]
                : this.menuSearchResults[0];
            this.navigateFromSearch(target);
        }
    }

    openMenuSearch(): void {
        this.menuSearchOpen = true;
        setTimeout(() => this.menuSearchInputRef?.nativeElement?.focus(), 50);
    }

    onMenuSearchInput(): void {
        const q = this.menuSearch.trim().toLowerCase();
        this.menuSearchActiveIndex = -1;
        if (!q) { this.menuSearchResults = []; return; }
        this.menuSearchResults = this.visibleNavItems
            .filter(item => !item.comingSoon && item.route && item.label.toLowerCase().includes(q))
            .slice(0, 8);
    }

    navigateFromSearch(item: NavItem): void {
        if (item.comingSoon || this.isNavLocked(item) || !item.route) return;
        if (this.tabService.isEnabled) {
            this.tabService.openTab(item.route, item.label, item.icon, item.labelKey);
        } else {
            this.router.navigate([item.route]);
        }
        // Clear state and release focus AFTER navigation starts so the newly-opened
        // page's own auto-focused input (code search, buyer picker, etc.) can take over
        this.menuSearch = '';
        this.menuSearchResults = [];
        this.menuSearchOpen = false;
        this.menuSearchActiveIndex = -1;
        this.menuSearchInputRef?.nativeElement?.blur();
    }

    /**
     * Handle a sidebar nav-item click.
     * On web/desktop: opens or activates the tab for this route.
     * On mobile/native: plain router navigation.
     */
    onNavItemClick(item: NavItem): void {
        if (this.isNavLocked(item) && (item.planFeature === 'production' || item.planFeature === 'storage_locations')) {
            this.router.navigate(['/store'], { queryParams: { category: 'DAIRY_ADDON' } });
            this.mobileOpen = false;
            this.desktopSidebarOpen = false;
            return;
        }
        if (item.comingSoon || this.isNavLocked(item) || !item.route) { return; }
        if (this.tabService.isEnabled) {
            this.tabService.openTab(item.route, item.label, item.icon, item.labelKey);
        } else {
            this.router.navigate([item.route]);
        }
        this.mobileOpen = false;
        this.desktopSidebarOpen = false;
    }

    toggleBottomDrawer(): void {
        this.showBottomDrawer = !this.showBottomDrawer;
    }

    setBottomDrawerPlacement(placement: 'bottom' | 'right'): void {
        this.bottomDrawerPlacement = placement;
        localStorage.setItem('crm-bottom-drawer-placement', placement);
    }

    onBottomNavItemClick(route: string, label: string, icon: string): void {
        if (this.isBottomNavRouteDisabled(route)) {
            return;
        }
        this.showBottomDrawer = false;
        const navItem = this.navItems.find(n => n.route === route);
        const labelKey = navItem?.labelKey || '';
        if (this.tabService.isEnabled) {
            this.tabService.openTab(route, label, icon, labelKey);
        } else {
            this.router.navigate([route]);
        }
    }

    onDrawerItemClick(item: NavItem): void {
        if (this.isNavLocked(item) && (item.planFeature === 'production' || item.planFeature === 'storage_locations')) {
            this.showBottomDrawer = false;
            this.router.navigate(['/store'], { queryParams: { category: 'DAIRY_ADDON' } });
            return;
        }
        if (item.comingSoon || this.isNavLocked(item)) { return; }
        this.showBottomDrawer = false;
        if (item.dialogAction === 'openDairyManager') {
            this.openEditDairy();
            return;
        }
        if (!item.route) { return; }
        if (this.tabService.isEnabled) {
            this.tabService.openTab(item.route, item.label, item.icon, item.labelKey);
        } else {
            this.router.navigate([item.route]);
        }
    }

    isBottomNavRouteDisabled(route: string): boolean {
        const item = this.navItems.find(n => n.route === route);
        if (!item) return false;
        if (item.alwaysVisible) return false;
        if (this.isNavLocked(item)) return true;
        return !!item.module && !this.permissionService.isAllowed(item.module as any, 'view');
    }

    getDrawerItemsBySection(sectionKey: string): NavItem[] {
        return this.visibleNavItems.filter(item => item.section === sectionKey);
    }

    getItemGradient(item: NavItem): string {
        const byRoute: Record<string, string> = {
            '/milk-collection':    'linear-gradient(135deg, #00b09b, #2dd4bf)',
            '/milk-sale':          'linear-gradient(135deg, #2563eb, #38bdf8)',
            '/payments':           'linear-gradient(135deg, #7c3aed, #a78bfa)',
            '/receipts':           'linear-gradient(135deg, #d97706, #fbbf24)',
            '/sales':              'linear-gradient(135deg, #0891b2, #06b6d4)',
            '/milk-bill':          'linear-gradient(135deg, #059669, #34d399)',
            '/members':            'linear-gradient(135deg, #4f46e5, #818cf8)',
            '/reports':            'linear-gradient(135deg, #0284c7, #7c3aed)',
            '/purchases':          'linear-gradient(135deg, #9333ea, #c084fc)',
            '/gst-sales':          'linear-gradient(135deg, #0369a1, #22d3ee)',
            '/gst-purchases':      'linear-gradient(135deg, #6d28d9, #a78bfa)',
            '/rate-list':          'linear-gradient(135deg, #be185d, #fb7185)',
            '/milk-dispatch':      'linear-gradient(135deg, #065f46, #34d399)',
            '/buyer-bill':         'linear-gradient(135deg, #b45309, #fcd34d)',
            '/route-milk-receipt': 'linear-gradient(135deg, #1d4ed8, #60a5fa)',
            '/chilling-bill':      'linear-gradient(135deg, #0e7490, #67e8f9)',
            '/daybook':            'linear-gradient(135deg, #92400e, #fbbf24)',
            '/ledger':             'linear-gradient(135deg, #312e81, #818cf8)',
            '/buyers':             'linear-gradient(135deg, #b45309, #f59e0b)',
            '/routes':             'linear-gradient(135deg, #065f46, #6ee7b7)',
            '/accounts':           'linear-gradient(135deg, #1e3a5f, #60a5fa)',
            '/products':           'linear-gradient(135deg, #134e4a, #2dd4bf)',
            '/route-report':       'linear-gradient(135deg, #7c2d12, #fb923c)',
            '/trial-balance':      'linear-gradient(135deg, #1e1b4b, #818cf8)',
            '/profit-loss':        'linear-gradient(135deg, #14532d, #4ade80)',
            '/balance-sheet':      'linear-gradient(135deg, #312e81, #a5b4fc)',
            '/milk-stock':         'linear-gradient(135deg, #831843, #f472b6)',
            '/stock-inventory':    'linear-gradient(135deg, #0369a1, #06b6d4)',
            '/dairy-users':        'linear-gradient(135deg, #4c1d95, #c084fc)',
            '/help':               'linear-gradient(135deg, #1e3a5f, #38bdf8)',
        };
        const bySection: Record<string, string> = {
            quick:    'linear-gradient(135deg, #0099ae, #00d4ff)',
            milk:     'linear-gradient(135deg, #059669, #34d399)',
            finance:  'linear-gradient(135deg, #d97706, #fbbf24)',
            masters:  'linear-gradient(135deg, #4f46e5, #818cf8)',
            reports:  'linear-gradient(135deg, #0284c7, #7c3aed)',
            settings: 'linear-gradient(135deg, #475569, #94a3b8)',
        };
        return byRoute[item.route] ?? bySection[item.section] ?? 'linear-gradient(135deg, #0099ae, #00d4ff)';
    }

    /**
     * Handle a pinned-shortcut click.
     * On web/desktop: opens or activates the corresponding tab.
     */
    onPinnedClick(shortcut: any): void {
        if (shortcut.soon || !shortcut.route) { return; }
        if (this.tabService.isEnabled) {
            const navItem = this.navItems.find(n => n.route === shortcut.route);
            this.tabService.openTab(shortcut.route, shortcut.label, navItem?.icon || 'pi-link', navItem?.labelKey || '');
        } else {
            this.router.navigate([shortcut.route]);
        }
        this.mobileOpen = false;
        this.desktopSidebarOpen = false;
    }

    /**
     * Close a tab from the tab bar close button.
     * Stops event propagation to prevent the tab's click handler from firing.
     */
    onCloseTab(event: MouseEvent, tabId: string): void {
        event.stopPropagation();
        this.tabService.closeTab(tabId);
        this.showTabOverflowMenu = false;
    }

    toggleTabOverflowMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.showTabSwitcher = false;
        this.showTabOverflowMenu = !this.showTabOverflowMenu;
    }

    activateOverflowTab(tab: AppTab): void {
        this.showTabOverflowMenu = false;
        this.tabService.activateTab(tab.id);
    }

    closeOverflowTab(event: MouseEvent, tabId: string): void {
        event.stopPropagation();
        this.tabService.closeTab(tabId);
    }

    openTabSwitcher(): void {
        if (this.tabService.tabs.length <= 1) { return; }
        this.showTabOverflowMenu = false;
        this.showTabSwitcher = true;
        this.tabSwitcherIndex = this.getCurrentTabSwitcherIndex();
        this.scheduleTabSwitcherScrollIntoView();
    }

    closeTabSwitcher(): void {
        this.showTabSwitcher = false;
    }

    toggleTabSwitcher(event: MouseEvent): void {
        event.stopPropagation();
        if (this.showTabSwitcher) {
            this.closeTabSwitcher();
            return;
        }
        this.openTabSwitcher();
    }

    setTabSwitcherIndex(index: number): void {
        this.tabSwitcherIndex = index;
        this.scheduleTabSwitcherScrollIntoView();
    }

    activateTabSwitcherTab(tab: AppTab): void {
        this.closeTabSwitcher();
        this.tabService.activateTab(tab.id);
    }

    closeTabSwitcherTab(event: MouseEvent, tabId: string): void {
        event.stopPropagation();
        this.tabService.closeTab(tabId);
        const nextMaxIndex = Math.max(0, this.tabService.tabs.length - 1);
        this.tabSwitcherIndex = Math.min(this.tabSwitcherIndex, nextMaxIndex);
        if (this.tabService.tabs.length <= 1) {
            this.closeTabSwitcher();
        }
    }

    isOverflowActive(): boolean {
        return this.overflowTabs.some(tab => tab.id === this.tabService.activeId);
    }

    /** True when the current router URL starts with the given route. */
    isActiveRoute(route: string): boolean {
        return this.router.url.startsWith(route);
    }

    /** True when current page is Sales or Purchases module. */
    isSalesOrPurchasePage(): boolean {
        const url = this.router.url || '';
        return url.startsWith('/sales') || url.startsWith('/purchases');
    }

    closeMenuSearch(): void {
        this.menuSearch = '';
        this.menuSearchResults = [];
        this.menuSearchOpen = false;
        this.menuSearchActiveIndex = -1;
    }

    private moveTabSwitcherSelection(step: number): void {
        const tabsCount = this.tabService.tabs.length;
        if (!tabsCount) { return; }
        this.tabSwitcherIndex = (this.tabSwitcherIndex + step + tabsCount) % tabsCount;
        this.scheduleTabSwitcherScrollIntoView();
    }

    private moveTabSwitcherByDirection(direction: 'left' | 'right' | 'up' | 'down'): void {
        const elements = this.tabSwitcherItemRefs?.toArray().map(ref => ref.nativeElement) || [];
        const count = elements.length;
        if (!count) { return; }

        const currentIndex = Math.max(0, Math.min(this.tabSwitcherIndex, count - 1));
        const currentEl = elements[currentIndex];
        if (!currentEl) { return; }

        if (direction === 'left') {
            this.tabSwitcherIndex = (currentIndex - 1 + count) % count;
            this.scheduleTabSwitcherScrollIntoView();
            return;
        }

        if (direction === 'right') {
            this.tabSwitcherIndex = (currentIndex + 1) % count;
            this.scheduleTabSwitcherScrollIntoView();
            return;
        }

        const currentTop = currentEl.offsetTop;
        const currentLeft = currentEl.offsetLeft;
        const verticalPadding = 2;

        const candidates = elements
            .map((el, index) => ({
                index,
                top: el.offsetTop,
                left: el.offsetLeft,
                distance: Math.abs(el.offsetLeft - currentLeft),
            }))
            .filter(item => {
                if (direction === 'down') {
                    return item.top > currentTop + verticalPadding;
                }
                return item.top < currentTop - verticalPadding;
            })
            .sort((a, b) => {
                if (direction === 'down') {
                    if (a.top !== b.top) { return a.top - b.top; }
                    return a.distance - b.distance;
                }
                if (a.top !== b.top) { return b.top - a.top; }
                return a.distance - b.distance;
            });

        if (!candidates.length) {
            this.tabSwitcherIndex = direction === 'down' ? 0 : count - 1;
            this.scheduleTabSwitcherScrollIntoView();
            return;
        }

        this.tabSwitcherIndex = candidates[0].index;
        this.scheduleTabSwitcherScrollIntoView();
    }

    private activateTabSwitcherSelection(): void {
        const tab = this.tabService.tabs[this.tabSwitcherIndex];
        if (!tab) { return; }
        this.activateTabSwitcherTab(tab);
    }

    private getCurrentTabSwitcherIndex(): number {
        const index = this.tabService.tabs.findIndex(tab => tab.id === this.tabService.activeId);
        return index >= 0 ? index : 0;
    }

    private scheduleTabSwitcherScrollIntoView(): void {
        if (!this.showTabSwitcher) { return; }
        setTimeout(() => this.scrollTabSwitcherItemIntoView(), 0);
    }

    private scrollTabSwitcherItemIntoView(): void {
        const items = this.tabSwitcherItemRefs?.toArray() || [];
        const target = items[this.tabSwitcherIndex]?.nativeElement;
        if (!target) { return; }
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    private scheduleTabOverflowRecalc(): void {
        if (typeof window === 'undefined') { return; }
        window.requestAnimationFrame(() => this.recomputeTabOverflow());
    }

    private recomputeTabOverflow(): void {
        const tabs = this.tabService.tabs;

        if (!this.tabService.isEnabled || !tabs.length) {
            this.visibleTabs = tabs;
            this.overflowTabs = [];
            this.showTabOverflowMenu = false;
            return;
        }

        const containerWidth = this.tabBarViewportRef?.nativeElement?.clientWidth || 0;
        if (!containerWidth) {
            this.visibleTabs = tabs;
            this.overflowTabs = [];
            return;
        }

        const gapWidth = 3;
        const overflowTriggerWidth = 42;
        const estimatedWidths = tabs.map(tab => this.estimateTabWidth(tab));
        const fullWidth = estimatedWidths.reduce((sum, width) => sum + width, 0) + Math.max(0, tabs.length - 1) * gapWidth;

        if (fullWidth <= containerWidth) {
            this.visibleTabs = tabs;
            this.overflowTabs = [];
            this.showTabOverflowMenu = false;
            return;
        }

        let availableWidth = Math.max(0, containerWidth - overflowTriggerWidth - gapWidth);
        const visibleIndexes: number[] = [];
        let usedWidth = 0;

        for (let index = 0; index < tabs.length; index++) {
            const nextWidth = estimatedWidths[index] + (visibleIndexes.length ? gapWidth : 0);
            if (usedWidth + nextWidth <= availableWidth || visibleIndexes.length === 0) {
                visibleIndexes.push(index);
                usedWidth += nextWidth;
                continue;
            }
            break;
        }

        const activeIndex = tabs.findIndex(tab => tab.id === this.tabService.activeId);
        if (activeIndex >= 0 && !visibleIndexes.includes(activeIndex)) {
            if (visibleIndexes.length) {
                visibleIndexes[visibleIndexes.length - 1] = activeIndex;
            } else {
                visibleIndexes.push(activeIndex);
            }
        }

        const visibleIndexSet = new Set(visibleIndexes);
        this.visibleTabs = tabs.filter((_, index) => visibleIndexSet.has(index));
        this.overflowTabs = tabs.filter((_, index) => !visibleIndexSet.has(index));

        if (!this.overflowTabs.length) {
            this.showTabOverflowMenu = false;
        }
    }

    private estimateTabWidth(tab: AppTab): number {
        const labelWidth = Math.min(110, Math.max(42, tab.label.length * 6.8));
        const closeWidth = tab.closable ? 24 : 0;
        return Math.min(200, Math.max(120, Math.round(46 + labelWidth + closeWidth)));
    }

    get filteredSidebarItems(): NavItem[] {
        const q = this.sidebarSearch.trim().toLowerCase();
        if (!q) return this.visibleNavItems;
        return this.visibleNavItems.filter(item => item.label.toLowerCase().includes(q));
    }

    toggleTheme(): void {
        this.isDarkTheme = !this.isDarkTheme;
        this.applyTheme();
    }

    toggleProfile(): void {
        this.showProfile = !this.showProfile;
    }

    closeProfile(): void {
        this.showProfile = false;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeProfile();
    }

    get isStaffUser(): boolean {
        return !!this.currentUser?.is_staff_user;
    }

    getUserRole(): string {
        if (this.isStaffUser) {
            return this.currentUser?.dairy_user_type || '—';
        }
        const r = this.currentUser?.role;
        return r?.role_name || r || '—';
    }

    // ── Edit Profile ──
    openEditProfile(): void {
        const u = this.currentUser;
        this.profileForm = this.fb.group({
            name:   [u?.name || u?.user_name || '', Validators.required],
            email:  [u?.email || ''],
            mobile: [u?.mobile || u?.mob || u?.phone_no || ''],
        });
        this.editProfileMsg = '';
        this.showEditProfile = true;
    }

    saveProfile(): void {
        if (this.profileForm.invalid || this.savingProfile) return;

        const userId =
            this.currentUser?.mu_id ||
            this.currentUser?.user_id ||
            this.currentUser?.userId ||
            this.currentUser?.u_id ||
            this.currentUser?.id ||
            this.authService.getUserId() ||
            Number(localStorage.getItem('milko_pending_user_id') || 0);
        if (!userId) {
            this.editProfileMsg = 'Unable to identify user. Please login again.';
            return;
        }

        const formValue = this.profileForm.value || {};
        const normalizedName = (formValue.name || '').trim();
        const normalizedEmail = (formValue.email || '').trim();
        const normalizedMobile = String(formValue.mobile || '').trim();

        if (!normalizedName) {
            this.editProfileMsg = 'Full name is required.';
            return;
        }

        this.savingProfile = true;
        const payload = {
            user_id: userId,
            id: userId,
            d_id: this.dairy?.id || 0,
            name: normalizedName,
            user_name: normalizedName,
            email: normalizedEmail,
            mobile: normalizedMobile,
            mob: normalizedMobile,
            phone_no: normalizedMobile,
        };
        this.api.post('users/edit_profile.php', payload).subscribe({
            next: (res: any) => {
                this.savingProfile = false;
                if (res?.success || res?.status) {
                    const updated = JSON.parse(JSON.stringify(this.currentUser));
                    Object.assign(updated, {
                        name: normalizedName,
                        user_name: normalizedName,
                        email: normalizedEmail,
                        mobile: normalizedMobile,
                        mob: normalizedMobile,
                        phone_no: normalizedMobile,
                    });
                    localStorage.setItem('milko_user', JSON.stringify(updated));
                    this.currentUser = updated;
                    this.editProfileMsg = 'success';
                    setTimeout(() => { this.showEditProfile = false; this.editProfileMsg = ''; }, 1500);
                } else {
                    this.editProfileMsg = res?.message || 'Update failed. Please try again.';
                }
            },
            error: (err: any) => { this.savingProfile = false; this.editProfileMsg = err?.error?.message || err?.message || 'Network error. Please try again.'; }
        });
    }

    doLoginAgain(): void {
        this.authService.logout();
    }

    // ── Create Dairy (first-login wizard) ──
    openCreateDairy(): void {
        // Plan limit pre-check (client-side; server also enforces this)
        if (!this.planService.canCreateDairy()) {
            const max  = this.planService.getMaxDairies();
            const plan = this.planService.getAccountPlanType();
            this.createDairyMsg = `Your ${plan} plan allows a maximum of ${max} dairy. Please upgrade your plan to create more.`;
            this.showCreateDairy = true;  // show dialog so user sees the message
            return;
        }

        const u = this.currentUser;
        this.createDairyForm = this.fb.group({
            company_name: ['', Validators.required],
            print_name:   [''],
            mobile:       [u?.mobile || ''],
            email:        [u?.email  || ''],
            address1:     [''],
            address2:     [''],
            states:       [''],
            city:         [''],
            pincode:      [''],
        });
        this.createDairyMsg = '';
        this.showCreateDairy = true;
    }

    submitCreateDairy(): void {
        if (this.createDairyForm.invalid || this.creatingDairy) return;
        this.creatingDairy    = true;
        this.createDairyMsg   = '';
        this.createDairyDbError = false;

        const u   = this.currentUser;
        const now = new Date().toISOString().slice(0, 10);
        const oneMonthDate = new Date();
        oneMonthDate.setMonth(oneMonthDate.getMonth() + 1);
        const oneMonthIso = oneMonthDate.toISOString().slice(0, 10);

        const resolvedUserId = this.authService.getUserId();
        // Prefer the authenticated account plan; pending signup state is only a fallback.
        const pendingPlan = (u?.user_plan_type || localStorage.getItem('milko_pending_plan') || 'BASIC').toUpperCase();
        // Enrich milkUser with all possible id aliases the PHP may look for
        const milkUserObj = {
            ...u,
            mu_id:          resolvedUserId,
            user_id:        resolvedUserId,
            id:             resolvedUserId,
            milkuser_id:    resolvedUserId,
            user_plan_type: pendingPlan,
            inc_per:        0,
            incentive_per:  0,
        };

        const payload = {
            ...this.createDairyForm.value,
            milkUser:       milkUserObj,
            milkuser_id:    resolvedUserId,
            created_id:     resolvedUserId,
            created_by:     u?.name || u?.user_name,
            soft_type:      'WEB',
            plan_soft:      pendingPlan,
            user_plan_type: pendingPlan,
            fin_year:       this.finYear,
            install_type:   'WEB-CRM',
            install_date:   now,
            purchase_date:  now,
            payment_status: 'Pending',
            product_key:    'DEMO-0001',
            amc_key:        'DEMO-AMC-0001',
            expiry_date:    oneMonthIso,
            amc_date:       oneMonthIso,
            sms_plan:       'NO',
            code:           '600000',
            inc_per:        0,
            incentive_per:  0,
            preAccounts:    this.defaultPreAccounts,
        };

        this.api.post('dairies/create_dairy_default_values.php', payload).subscribe({
            next: (res: any) => {
                this.creatingDairy = false;
                if (res?.success || res?.status) {
                    this.createDairyMsg = 'success';
                    localStorage.removeItem('milko_pending_plan');
                    this.finishDairyOnboarding(1800);
                } else {
                    // A returned dairy ID means the main insert succeeded and only a later
                    // default-data step failed, so continue by refreshing the session.
                    if (res?.dairy_id || res?.d_id) {
                        this.createDairyDbError = true;
                        this.createDairyMsg = 'warning';
                        this.finishDairyOnboarding(3000);
                    } else {
                        this.createDairyMsg = res?.error || res?.message || 'Failed to create dairy. Please try again.';
                    }
                }
            },
            error: (err: any) => {
                this.creatingDairy  = false;
                this.createDairyMsg = err?.error?.message || 'Network error. Please try again.';
            }
        });
    }

    private finishDairyOnboarding(delayMs: number, attempt = 0): void {
        this.creatingDairy = true;
        setTimeout(() => {
            this.authService.refreshCurrentUser().subscribe(user => {
                if (user && this.authService.getDairyId() > 0) {
                    localStorage.removeItem('milko_pending_user_id');
                    localStorage.removeItem('milko_dairy_created');
                    window.location.href = `${window.location.origin}/#/dashboard`;
                    return;
                }

                if (attempt < 1) {
                    this.finishDairyOnboarding(1200, attempt + 1);
                    return;
                }

                this.creatingDairy = false;
                this.createDairyDbError = false;
                this.createDairyMsg = 'Your dairy was created, but the new data could not be loaded. Please refresh the page to continue.';
            });
        }, delayMs);
    }

    // ── Edit Dairy ──
    openEditDairy(): void {
        const d = this.dairy;
        this.dairyForm = this.fb.group({
            // Basic
            company_name:   [d?.company_name   || '', Validators.required],
            print_name:     [d?.print_name     || ''],
            email:          [d?.email          || ''],
            mobile:         [d?.mobile         || ''],
            phone_no:       [d?.phone_no       || ''],
            // Address
            address1:       [d?.address1       || ''],
            address2:       [d?.address2       || ''],
            states:         [d?.states         || ''],
            city:           [d?.city           || ''],
            pincode:        [d?.pincode        || ''],
            // Business
            gst_no:         [d?.gst_no         || ''],
            state_code:     [d?.state_code     || ''],
            pan_no:         [d?.pan_no         || ''],
            com_lic_no:     [d?.com_lic_no     || ''],
            // Banking
            bank_name:      [d?.bank_name      || ''],
            bank_ac:        [d?.bank_ac        || ''],
            ifs_code:       [d?.ifs_code       || ''],
            // App preferences
            transaction_terminology: [this.transactionTerminology.mode],
        });
        this.editDairyMsg = '';
        this.showEditDairy = true;
        this.showProfile = false;
    }

    saveDairy(): void {
        if (this.dairyForm.invalid || this.savingDairy) return;
        this.savingDairy = true;
        const transactionTerminology = this.dairyForm.value.transaction_terminology as TransactionTerminologyMode;
        const { transaction_terminology, ...dairyPayload } = this.dairyForm.value;

        const normalizeMode = (value: any): string =>
            String(value || '').trim().toLowerCase().replace(/[\s/-]+/g, '_');

        const currentConfig = (this.dairy as any)?.configurations;
        const currentTerminology = normalizeMode(
            Array.isArray(currentConfig)
                ? currentConfig[0]?.transaction_terminology
                : currentConfig?.transaction_terminology
        );
        const nextTerminology = normalizeMode(transactionTerminology);
        const terminologyChanged = !!nextTerminology && nextTerminology !== currentTerminology;

        const hasDairyChanges = Object.keys(dairyPayload).some((key) => {
            const currentValue = (this.dairy as any)?.[key] ?? null;
            const nextValue = (dairyPayload as any)?.[key] ?? null;
            return String(currentValue ?? '').trim() !== String(nextValue ?? '').trim();
        });

        if (!hasDairyChanges && !terminologyChanged) {
            this.savingDairy = false;
            this.transactionTerminology.setMode(transactionTerminology);
            const updated = JSON.parse(JSON.stringify(this.currentUser));
            if (updated.dairies?.[0]) {
                updated.dairies[0] = {
                    ...updated.dairies[0],
                    configurations: {
                        ...(updated.dairies[0].configurations || {}),
                        transaction_terminology: transactionTerminology,
                    },
                };
            }
            localStorage.setItem('milko_user', JSON.stringify(updated));
            this.currentUser = updated;
            this.editDairyMsg = 'success';
            setTimeout(() => { this.showEditDairy = false; this.editDairyMsg = ''; }, 1200);
            return;
        }

        const payload = {
            ...dairyPayload,
            transaction_terminology: transactionTerminology,
            id: this.dairy?.id,       // PHP requires 'id', not 'd_id'
        };
        this.api.post('dairies/edit_dairy.php', payload).subscribe({
            next: (res: any) => {
                this.savingDairy = false;
                if (res?.success || res?.status) {
                    this.transactionTerminology.setMode(transactionTerminology);
                    const updated = JSON.parse(JSON.stringify(this.currentUser));
                    if (updated.dairies?.[0]) {
                        updated.dairies[0] = {
                            ...updated.dairies[0],
                            ...dairyPayload,
                            configurations: {
                                ...(updated.dairies[0].configurations || {}),
                                transaction_terminology: transactionTerminology,
                            },
                        };
                    }
                    localStorage.setItem('milko_user', JSON.stringify(updated));
                    this.currentUser = updated;
                    this.editDairyMsg = 'success';
                    setTimeout(() => { this.showEditDairy = false; this.editDairyMsg = ''; }, 1500);
                } else {
                    this.editDairyMsg = res?.message || 'Update failed. Please try again.';
                }
            },
            error: (err: any) => {
                this.savingDairy = false;
                this.editDairyMsg = err?.userMessage || err?.error?.message || err?.message || 'Save failed. Please try again.';
                console.error('Dairy edit error:', err);
            }
        });
    }

    // ── Change Password ──
    openChangePassword(): void {
        this.passwordForm = this.fb.group({
            current_password: ['', Validators.required],
            new_password:     ['', [Validators.required, Validators.minLength(6)]],
            confirm_password: ['', Validators.required],
        }, { validators: (fg: FormGroup) => {
            const np = fg.get('new_password')?.value;
            const cp = fg.get('confirm_password')?.value;
            return np && cp && np !== cp ? { mismatch: true } : null;
        }});
        this.changePwMsg = '';
        this.showPwdSessionChoice = false;
        this.pwdChangeToken = '';
        this.showChangePassword = true;
        this.showProfile = false;
    }

    savePassword(): void {
        if (this.passwordForm.invalid || this.savingPassword) return;
        this.savingPassword = true;
        const payload = {
            d_id:             this.dairy?.id || 0,
            user_id:          this.authService.getUserId(),
            current_password: this.passwordForm.value.current_password,
            new_password:     this.passwordForm.value.new_password,
        };
        this.api.post('users/change_password.php', payload).subscribe({
            next: (res: any) => {
                this.savingPassword = false;
                if (res?.success || res?.status) {
                    this.pwdChangeToken = res?.token || '';
                    this.changePwMsg = 'success';
                    this.showPwdSessionChoice = true;
                } else {
                    this.changePwMsg = res?.message || 'Incorrect current password.';
                }
            },
            error: (err: any) => { this.savingPassword = false; this.changePwMsg = err?.userMessage || err?.errorBody?.message || err?.error?.message || 'Network error. Please try again.'; }
        });
    }

    staySignedInAfterPwdChange(): void {
        if (this.pwdChangeToken) {
            this.authService.updateToken(this.pwdChangeToken);
        }
        this.showChangePassword = false;
        this.showPwdSessionChoice = false;
        this.pwdChangeToken = '';
    }

    signOutAfterPwdChange(): void {
        this.showChangePassword = false;
        this.authService.logout();
    }

    // ── Delete Account ──
    openDeleteAccount(): void {
        this.deleteConfirmText = '';
        this.deleteAccountPassword = '';
        this.deleteAccountPasswordVisible = false;
        this.deleteAccountMsg = '';
        this.deletingAccount = false;
        this.showDeleteAccount = true;
        this.showProfile = false;
    }

    confirmDeleteAccount(): void {
        if (this.deleteConfirmText !== 'DELETE' || !this.deleteAccountPassword || this.deletingAccount) return;
        this.deletingAccount = true;
        this.deleteAccountMsg = '';
        this.api.post<any>('users/delete_account.php', {
            user_id: this.authService.getUserId(),
            d_id: this.authService.getDairyId(),
            password: this.deleteAccountPassword
        }).subscribe({
            next: (res: any) => {
                if (res?.success) {
                    this.authService.logout();
                } else {
                    this.deletingAccount = false;
                    this.deleteAccountMsg = res?.message || 'Delete failed. Please try again.';
                }
            },
            error: (err: any) => {
                this.deletingAccount = false;
                this.deleteAccountMsg = err?.userMessage || err?.error?.message || 'Server error. Please try again.';
            }
        });
    }

    private applyTheme(): void {
        document.body.classList.remove('theme-dark', 'theme-light');
        document.body.classList.add(this.isDarkTheme ? 'theme-dark' : 'theme-light');
        localStorage.setItem('crm-theme', this.isDarkTheme ? 'dark' : 'light');
        // Sync status bar with theme.
        // Both platforms use overlay:true so only icon style needs updating.
        // Style.Dark = white icons (dark bg), Style.Light = black icons (light bg).
        if (Capacitor.isNativePlatform()) {
            import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
                StatusBar.setStyle({ style: this.isDarkTheme ? Style.Dark : Style.Light }).catch(() => {});
            });
        }
    }

    logout(): void {
        this.authService.logout();
    }

    getInitial(): string {
        const name = this.currentUser?.name || 'U';
        return name.charAt(0).toUpperCase();
    }
}
